import { useState } from 'react'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
	Copy,
	CheckCircle2,
	Bot,
	ChevronDown,
	ChevronUp,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'

export const AIPromptCard = ({ title, prompt }: { title: string; prompt: string }) => {
	const { toast } = useToast()
	const [isExpanded, setIsExpanded] = useState(false)
	const [copied, setCopied] = useState(false)

	const handleCopy = () => {
		navigator.clipboard
			.writeText(prompt)
			.then(() => {
				setCopied(true)
				toast({
					title: 'AI Prompt Copied!',
					description:
						'Paste this into Cursor AI or your preferred AI assistant.',
				})
				setTimeout(() => setCopied(false), 2000)
			})
			.catch(() => {
				toast({
					title: 'Error',
					description: 'Failed to copy prompt',
					variant: 'destructive',
				})
			})
	}

	return (
		<Card className="mb-8 shadow-sm dark:bg-gray-800/50 border border-purple-200 dark:border-purple-700">
			<CardHeader
				className="cursor-pointer"
				onClick={() => setIsExpanded(!isExpanded)}>
				<div className="flex items-center justify-between">
					<CardTitle className="text-lg flex items-center gap-2">
						<Bot className="h-5 w-5 text-purple-600" />
						{title}
					</CardTitle>
					<div className="flex items-center gap-2">
						<Button
							onClick={(e) => {
								e.stopPropagation()
								handleCopy()
							}}
							variant={copied ? 'outline' : 'secondary'}
							size="sm"
							className={cn(
								'transition-all duration-200',
								copied
									? 'bg-green-50 dark:bg-green-900/50 text-green-600 dark:text-green-300 border-green-200 dark:border-green-700'
									: ''
							)}>
							{copied ? (
								<>
									<CheckCircle2 className="mr-2 h-4 w-4" />
									Copied!
								</>
							) : (
								<>
									<Copy className="mr-2 h-4 w-4" />
									Copy Prompt
								</>
							)}
						</Button>
						{isExpanded ? (
							<ChevronUp className="h-5 w-5 text-gray-500" />
						) : (
							<ChevronDown className="h-5 w-5 text-gray-500" />
						)}
					</div>
				</div>
				<CardDescription className="dark:text-gray-400">
					{isExpanded
						? 'Click to collapse'
						: 'Click to expand and see the full prompt'}
				</CardDescription>
			</CardHeader>
			{isExpanded && (
				<CardContent>
					<div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-md border">
						<pre className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300 font-mono">
							{prompt}
						</pre>
					</div>
				</CardContent>
			)}
		</Card>
	)
}